/**
 * Persisted player preferences.
 *
 * A thin, typed wrapper over `localStorage` under the `skelestick.` prefix.
 *
 * Hard rule: **this never throws.** `localStorage` can be absent (SSR-ish
 * contexts), disabled (Safari private mode historically threw on write) or full,
 * and stored values can be hand-edited garbage. Every read validates and falls
 * back to a default; every write swallows quota errors. A corrupt preference
 * must never cost the player a run.
 */

import { MAX_NAME_LEN, PALETTE } from '@shared/constants';

const PREFIX = 'skelestick.';

/** `color` sentinel: let the server assign the least-used hue. */
export const COLOR_AUTO = -1;

const SENSITIVITY_MIN = 0.3;
const SENSITIVITY_MAX = 2;

const DEFAULTS = {
  name: '',
  color: COLOR_AUTO,
  best: 0,
  mute: false,
  sensitivity: 1,
  summitCount: 0,
  checkpoint: null,
} as const;

// ---------------------------------------------------------------------------
// storage primitives
// ---------------------------------------------------------------------------

/** `null` when storage is unavailable — every accessor then uses defaults. */
function store(): Storage | null {
  try {
    // Touching `localStorage` itself can throw when cookies are blocked.
    const s = globalThis.localStorage;
    return s ?? null;
  } catch {
    return null;
  }
}

function readRaw(key: string): string | null {
  try {
    return store()?.getItem(PREFIX + key) ?? null;
  } catch {
    return null;
  }
}

function writeRaw(key: string, value: string): void {
  try {
    store()?.setItem(PREFIX + key, value);
  } catch {
    /* quota exceeded or storage disabled — preferences are not worth an exception */
  }
}

/** Parse a stored JSON value, or `undefined` if it is missing or malformed. */
function readJson(key: string): unknown {
  const raw = readRaw(key);
  if (raw === null) return undefined;
  try {
    return JSON.parse(raw) as unknown;
  } catch {
    return undefined;
  }
}

function readNumber(key: string, fallback: number): number {
  const v = readJson(key);
  return typeof v === 'number' && Number.isFinite(v) ? v : fallback;
}

function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}

// ---------------------------------------------------------------------------
// validation
// ---------------------------------------------------------------------------

/**
 * Trim, collapse whitespace and cut to `MAX_NAME_LEN`. The server sanitises
 * again (it must — clients lie); this only keeps the start-screen honest.
 */
export function sanitizeName(raw: string): string {
  return raw
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, MAX_NAME_LEN);
}

/** A colour is valid only if it is `COLOR_AUTO` or an actual palette entry. */
export function isPaletteColor(v: number): boolean {
  return v === COLOR_AUTO || PALETTE.includes(v);
}

/**
 * A claimed checkpoint, pinned to the level it was claimed on.
 *
 * `h` is the level hash and `i` the index into `level.checkpoints`. The hash is
 * the whole point: regenerate the mountain and pad 3 is somewhere else entirely,
 * so a save from the old layout has to be ignored rather than teleport the
 * player into rock.
 */
export interface CheckpointSave {
  /** `hashLevel(level)` of the level this was claimed on. */
  h: string;
  /** Index into `level.checkpoints`. */
  i: number;
}

/** Narrow a parsed JSON value to a `CheckpointSave`, or `null` if it is not one. */
function asCheckpointSave(v: unknown): CheckpointSave | null {
  if (typeof v !== 'object' || v === null) return null;
  const { h, i } = v as { h?: unknown; i?: unknown };
  if (typeof h !== 'string' || h === '') return null;
  if (typeof i !== 'number' || !Number.isInteger(i) || i < 0) return null;
  return { h, i };
}

// ---------------------------------------------------------------------------
// the settings object
// ---------------------------------------------------------------------------

export interface Settings {
  /** Display name, already sanitised. Empty means "never chosen one". */
  name: string;
  /** A `PALETTE` entry, or `COLOR_AUTO` (-1) to let the server pick. */
  color: number;
  /** Personal best altitude in metres. */
  best: number;
  /** Audio muted. */
  mute: boolean;
  /** Mouse-look multiplier, clamped to `[0.3, 2]`. */
  sensitivity: number;
  /** How many times this browser has reached the summit. */
  summitCount: number;
  /**
   * The highest checkpoint claimed on this level, so a reload resumes the climb
   * instead of dropping the player back at the plaza. `null` means "no claim" —
   * which is also what a save from a different level layout reads back as, since
   * the caller compares `h` against the current level hash.
   */
  checkpoint: CheckpointSave | null;
  /** Wipe every stored preference (used by nothing yet; handy in the console). */
  clear(): void;
}

export const settings: Settings = {
  get name(): string {
    const v = readJson('name');
    return typeof v === 'string' ? sanitizeName(v) : DEFAULTS.name;
  },
  set name(v: string) {
    writeRaw('name', JSON.stringify(sanitizeName(v)));
  },

  get color(): number {
    const v = readNumber('color', DEFAULTS.color);
    return isPaletteColor(v) ? v : DEFAULTS.color;
  },
  set color(v: number) {
    writeRaw('color', JSON.stringify(isPaletteColor(v) ? v : COLOR_AUTO));
  },

  get best(): number {
    const v = readNumber('best', DEFAULTS.best);
    return v > 0 ? v : 0;
  },
  set best(v: number) {
    writeRaw('best', JSON.stringify(Number.isFinite(v) && v > 0 ? v : 0));
  },

  get mute(): boolean {
    const v = readJson('mute');
    return typeof v === 'boolean' ? v : DEFAULTS.mute;
  },
  set mute(v: boolean) {
    writeRaw('mute', JSON.stringify(v === true));
  },

  get sensitivity(): number {
    return clamp(readNumber('sensitivity', DEFAULTS.sensitivity), SENSITIVITY_MIN, SENSITIVITY_MAX);
  },
  set sensitivity(v: number) {
    const n = Number.isFinite(v) ? clamp(v, SENSITIVITY_MIN, SENSITIVITY_MAX) : DEFAULTS.sensitivity;
    writeRaw('sensitivity', JSON.stringify(n));
  },

  get summitCount(): number {
    const v = readNumber('summitCount', DEFAULTS.summitCount);
    return v > 0 ? Math.floor(v) : 0;
  },
  set summitCount(v: number) {
    writeRaw('summitCount', JSON.stringify(Number.isFinite(v) && v > 0 ? Math.floor(v) : 0));
  },

  get checkpoint(): CheckpointSave | null {
    return asCheckpointSave(readJson('checkpoint'));
  },
  set checkpoint(v: CheckpointSave | null) {
    // An invalid value clears rather than corrupts: the getter would reject it
    // anyway, and a stored `null` is indistinguishable from a missing key.
    writeRaw('checkpoint', JSON.stringify(asCheckpointSave(v)));
  },

  clear(): void {
    const s = store();
    if (s === null) return;
    for (const key of Object.keys(DEFAULTS)) {
      try {
        s.removeItem(PREFIX + key);
      } catch {
        /* ignore */
      }
    }
  },
};

export { SENSITIVITY_MIN, SENSITIVITY_MAX };
